import { getCookie } from 'cookies-next'
import { cookieKeys } from 'lib/consts/browserStorage'
import { resolveDeviceSession } from 'lib/helpers/resolveDeviceSession/resolveDeviceSession'
import { logError } from 'lib/tracking/datadog'
import { getSocureErrorReport } from 'lib/tracking/helpers/getSocureErrorReport/getSocureErrorReport'
import { isExpectedSocureSessionFailure } from 'lib/tracking/helpers/isExpectedSocureSessionFailure/isExpectedSocureSessionFailure'

// Lazy-loaded to keep the browser-only SDK out of SSR/main bundles. It's a UMD bundle whose export IS
// the SigmaDeviceManager class, so it arrives via the interop default.
const loadSocureManager = async () => (await import('@socure-inc/device-risk-sdk')).default

const ASSOCIATION_FAILURE_MESSAGE = 'Socure customer session association failed'

const getErrorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error))

// SigmaDeviceManager is a singleton; init / add / rekey all mutate the same device-session window, and
// the initializer fires them un-awaited — so a rapid auth change (e.g. login then a token refresh) can
// re-fire it mid-flight and interleave their POSTs, keying the session to whichever response lands last.
// Funnel every operation through one queue so they apply in submission order. The tail settles regardless
// of an operation's outcome, so a rejected op can't wedge the queue for the next — a hung, never-settling
// SDK call still would, which is acceptable here: these are background telemetry calls that settle within
// network timeouts.
let operationQueue: Promise<unknown> = Promise.resolve()

const enqueueSocureOperation = <T>(operation: () => Promise<T>): Promise<T> => {
  // The tail below swallows both branches, so operationQueue always settles fulfilled — a single
  // fulfillment handler runs the next operation; no rejection handler is reachable here.
  const result = operationQueue.then(operation)

  operationQueue = result.then(
    () => undefined,
    () => undefined,
  )

  return result
}

interface InitSocureParams {
  sdkKey: string
  /**
   * Omit to initialize the device session unbound. Socure binds a `customerSessionId` to exactly one
   * session window; re-binding an already-bound id is rejected with a 400 "Customer session already
   * exists". So a context that resumes an id another tab/load already bound must initialize without it
   * — it stays linked to the device (the shared device cookie), and the existing binding stands.
   */
  customerSessionId?: string
}

const initSocureCore = async ({ sdkKey, customerSessionId }: InitSocureParams): Promise<boolean> => {
  try {
    const SigmaDeviceManager = await loadSocureManager()

    SigmaDeviceManager.initialize(customerSessionId ? { sdkKey, customerSessionId } : { sdkKey })

    return true
  } catch (error) {
    if (!isExpectedSocureSessionFailure({ message: getErrorMessage(error) })) {
      const report = getSocureErrorReport({ thrown: error, fallbackMessage: 'Socure initialization failed' })

      logError(report.error, report.context)
    }

    return false
  }
}

const rekeySocureCore = async (customerSessionId: string): Promise<void> => {
  try {
    const SigmaDeviceManager = await loadSocureManager()

    await SigmaDeviceManager.createNewSession(customerSessionId)
  } catch (error) {
    if (isExpectedSocureSessionFailure({ message: getErrorMessage(error) })) {
      return
    }

    const report = getSocureErrorReport({ thrown: error, fallbackMessage: 'Socure session re-key failed' })

    logError(report.error, report.context)
  }
}

const addSocureCore = async (customerSessionId: string): Promise<void> => {
  try {
    const SigmaDeviceManager = await loadSocureManager()

    // Gate the fold on session readiness: addCustomerSessionId associates against the live device-session
    // window, and firing it before that window is established gets the association rejected (ingestion
    // 401), losing the fold. getSessionToken resolves once the SDK's device session is ready. Best-effort —
    // a probe rejection is swallowed so it can't surface as a bogus association failure; the association
    // below still runs and drives the fail-open recovery.
    await SigmaDeviceManager.getSessionToken().catch(() => undefined)

    await SigmaDeviceManager.addCustomerSessionId(customerSessionId)
  } catch (error) {
    if (isExpectedSocureSessionFailure({ message: getErrorMessage(error) })) {
      return
    }

    // Logged under our own constant message: the SDK's "Failed to associate customer session id" message
    // is filtered out as console noise in beforeSend, and the diagnostic context must survive to RUM.
    const { context } = getSocureErrorReport({ thrown: error, fallbackMessage: ASSOCIATION_FAILURE_MESSAGE })

    logError(new Error(ASSOCIATION_FAILURE_MESSAGE), context)

    await rekeySocureCore(customerSessionId)
  }
}

const forgetSocureCore = async (): Promise<void> => {
  if (!getCookie(cookieKeys.socureDeviceSession)) {
    return
  }

  const { id, action } = resolveDeviceSession(null)

  if (action !== 'resume') {
    await rekeySocureCore(id)
  }
}

/**
 * Initializes the Socure SDK. When `customerSessionId` is given the device session is bound to it
 * (reusing the same id across loads/tabs links them into one device session); when omitted the
 * session is device-linked but unbound. Resolves to whether init succeeded. Serialized against the
 * other session operations so it can't interleave with a concurrent re-key or fold.
 */
export const initSocure = (params: InitSocureParams): Promise<boolean> =>
  enqueueSocureOperation(() => initSocureCore(params))

/**
 * Stops the in-flight Socure session and starts a fresh one under `customerSessionId`. Fails open; an
 * expected device-session-window failure ({@link isExpectedSocureSessionFailure}) is swallowed rather
 * than reported. Serialized against the other session operations.
 */
export const rekeySocureSession = (customerSessionId: string): Promise<void> =>
  enqueueSocureOperation(() => rekeySocureCore(customerSessionId))

/**
 * Folds the pre-login anonymous activity into the login's `customerSessionId` by associating it with the
 * running session. Fails open. Two failure modes:
 *
 * - The device session window can't be established (device cookie gone) — expected and unrecoverable, and
 *   re-keying would hit the same wall, so it's swallowed.
 * - The window exists but the association POST itself failed — recoverable, so it's reported and re-keyed
 *   to a fresh session under `customerSessionId` (the pre-login fold is lost for that session).
 *
 * Serialized against the other session operations, so a concurrent re-auth can't interleave its POSTs.
 */
export const addSocureSession = (customerSessionId: string): Promise<void> =>
  enqueueSocureOperation(() => addSocureCore(customerSessionId))

/**
 * Re-keys to a fresh anonymous session — call where the auth token is removed (sign-out / expiry).
 * SocureDeviceInitializer can't observe sign-out (the token cookie is deleted after the post-logout
 * render reads it), so without this the prior login's id keeps attaching to later anonymous activity.
 * No-op when Socure isn't active. Serialized against the other session operations.
 */
export const forgetSocureSession = (): Promise<void> => enqueueSocureOperation(() => forgetSocureCore())
